/* istanbul ignore file */
/** @jsx jsx */
import { jsx } from 'theme-ui';
import React, { useState, memo } from 'react';
import {
  Box,
  VStack,
  Checkbox,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Heading,
  useToast,
  ModalCloseButton,
  Center,
  ButtonGroup,
  IconButton,
} from '@chakra-ui/core';
import { css } from '@emotion/core';
import { RiAddCircleLine, RiIndeterminateCircleLine } from 'react-icons/ri';
import isNil from 'lodash/isNil';
import { useTranslation } from 'react-i18next';
import axios, { AxiosError } from 'axios';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import colors from '../../../../../styles/colors';
import { useSeekerContainerContext } from '../../SeekerContainerContext';
import { useStoreActions } from '../../../../../models/hooks';
import { ReactComponent as _IconCalendar } from '../../../../../assets/img/icon-calendar-blue.svg';
import { backgroundCheckStyle, confirmationModalHeaderCSS } from './ConfirmationModal.styles';
import { reactDatePickerCSS } from '../../../../calendar/scheduleSidebar/ReactDatePicker.Style';

const datePickerStyle = css`
  .react-datepicker-wrapper {
    display: block;
    width: 100%;
  }
`;

export type I9CheckModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const IconCalendar = memo(_IconCalendar);

export const I9CheckModal = ({ isOpen, onClose }: I9CheckModalProps): JSX.Element => {
  const { t, i18n } = useTranslation('messaging');
  const toast = useToast();
  const { selectedCandidate } = useSeekerContainerContext();

  const I9CheckRequest = useStoreActions((actions) => actions.messaging.requestI9Check);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [targetDate, setTargetDate] = useState<Date | null>(null);

  const showFullDescription = (id: string) => {
    const selectElement = document.getElementById(id);
    if (isNil(selectElement)) {
      return;
    }
    selectElement.classList.toggle('more');
  };

  const CustomDatePickerInput = /* istanbul ignore next */ (
    props: React.HTMLProps<HTMLInputElement>,
    ref: React.Ref<HTMLInputElement>,
  ) => (
    <ButtonGroup
      d="flex"
      className="customDatepicker"
      size="md"
      isAttached
      variant="outline"
      onClick={props.onClick}
      ref={ref}
      border="1px solid"
      borderColor="gray.300"
      borderRadius="6px"
      _focus={{ borderColor: 'blue.500', boxShadow: 'outline' }}
      placeholder={props.placeholder}
    >
      <Button fontWeight="normal" mr="-px" borderColor="gray.150" style={{ width: '100%' }} justifyContent="flex-start">
        {props.value}
      </Button>
      <IconButton
        borderColor="gray.150"
        style={{ padding: '0.2rem' }}
        aria-label="calendar"
        icon={<IconCalendar width={20} height={20} />}
      />
    </ButtonGroup>
  );

  const submitI9CheckRequest = async () => {
    try {
      if (selectedCandidate) {
        setIsLoading(true);

        await I9CheckRequest({ candidateId: selectedCandidate?.id, startingDate: targetDate });
        onClose();
        toast({
          title: t('actions.i9Check.modal.successMessage'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      let defaultMessage = t('actions.i9Check.modal.errorDefaultToast');
      const newError = error as AxiosError<{ message: string }>;
      if (axios.isAxiosError(newError)) {
        if (newError?.response?.status === 422) {
          defaultMessage = t('actions.i9Check.modal.error422Toast');
        } else if (newError?.response?.status === 500) {
          defaultMessage = t('actions.i9Check.modal.error500Toast');
        } else {
          defaultMessage = newError.response?.data?.message || defaultMessage;
        }
      }
      toast({
        description: defaultMessage,
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal id="I9CheckModal" isOpen={isOpen} onClose={onClose} isCentered closeOnOverlayClick size="xl">
      <ModalOverlay>
        <ModalContent py={4} px={2} borderRadius="20px">
          <ModalHeader css={confirmationModalHeaderCSS} className="modal-header">
            <Flex direction="row" justifyContent="center">
              <Heading fontSize="24px" color={colors.black} fontWeight={500}>
                {t('actions.i9Check.modal.title')}
              </Heading>
            </Flex>
            <Center>
              <Text fontSize="sm" color="#A0AEC0">
                {t('actions.i9Check.modal.submittedDate')}
              </Text>
              &nbsp;
              <Text fontSize="sm">{moment(Date.now()).format('L')}</Text>
            </Center>
          </ModalHeader>
          <ModalCloseButton mr={4} mt={2} />
          <ModalBody>
            <VStack alignItems="flex-start">
              <Box marginBottom="15px" mt={3} className="bc-content" css={backgroundCheckStyle}>
                <Flex justifyContent="space-between" alignItems="center">
                  <Text my="8px" fontWeight={700} mr={6}>
                    {t('actions.i9Check.modal.targetDate')}
                    <Text as="span" color={colors.red[500]} ml={1}>
                      *
                    </Text>
                  </Text>
                  <Box flex="1" ml={20} css={reactDatePickerCSS} data-testid="datePicker">
                    <Box css={datePickerStyle}>
                      <DatePicker
                        placeholderText="Select Date"
                        selected={targetDate}
                        className="react-datepicker-input-fullwidth bold"
                        customInput={React.createElement(React.forwardRef(CustomDatePickerInput))}
                        locale={i18n.language}
                        dateFormat="P"
                        minDate={moment().toDate()}
                        data-testid="targetDate"
                        shouldCloseOnSelect
                        onChange={(date) => {
                          setTargetDate(date as Date);
                        }}
                      />
                    </Box>
                  </Box>
                </Flex>
                <Checkbox colorScheme="blue" alignItems="flex-start" key="1" value="1" isChecked mt={5}>
                  <Box pl="20px">
                    <Heading as="h4" lineHeight="1.1" marginBottom="5px" fontSize="md">
                      {t('actions.i9Check.label')}
                    </Heading>
                  </Box>
                </Checkbox>
                <Flex
                  data-testid="showFullDescription"
                  pl="42px"
                  id="1"
                  className="bc-description"
                  onClick={() => showFullDescription('1')}
                >
                  <Text fontSize="sm">{t('actions.i9Check.modal.body')}</Text>
                  <Box color={colors.blue[500]} fontSize="lg" pl={3}>
                    <RiAddCircleLine className="add-icon" />
                    <RiIndeterminateCircleLine className="remove-icon" />
                  </Box>
                </Flex>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              data-testid="submitBtn"
              isLoading={isLoading}
              colorScheme="blue"
              onClick={submitI9CheckRequest}
              disabled={isNil(targetDate)}
            >
              {t('actions.i9Check.modal.submitBtn')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
};
